import { TWidget } from 'widgets/Widget';
import { debounce } from 'widgets/toolbox/debounce';

declare const window: ExtendedWindow;

type TFitPredictorContext = {
    site: {
        currency: string;
        language: string;
        market: string;
    };
    product: {
        productId: string;
        brand: string;
        gender: string;
        department: string;
        color: string;
        size?: string;
        sizes: Array<string>;
        availableSizes: Array<string>;
        sizeType?: string;
        price: string;
        salesPrice?: string;
        sizeSystem?: string;
        sizeConversion?: string;
    };
} | null;

type TFitPredictorCommonEventData = {
    feature: string;
    item_id: string;
    item_variant: string;
} | null;

/**
 * @description FitPredictor implementation
 * @param Widget Base widget for extending
 * @returns Fit Predictor widget
 */
function FitPredictorClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory product
     * @class FitPredictor
     * @augments Widget
     */
    class FitPredictor extends Widget {
        activeRegion = '';

        productId = '';

        productContextString = '';

        sizeSelected = '';

        DEBOUNCE_DELAY = 200;

        prefs() {
            return {
                fpContext: null as TFitPredictorContext,
                ...super.prefs()
            };
        }

        init(): void {
            super.init();

            const prefs = this.prefs();

            if (window.ssp && prefs.fpContext) {
                this.activeRegion = prefs.fpContext.product.sizeSystem || '';
                this.productId = prefs.fpContext.product.productId;
                this.productContextString = JSON.stringify(prefs.fpContext.product);

                window.ssp('set', 'site', prefs.fpContext.site);
                window.ssp('set', 'page', { type: 'pdp' });

                const userData = window.sfccUnCachedData?.customerData;

                if (userData && !userData.isAnonymous && userData.emailHash) {
                    window.ssp('set', 'user', {
                        userId: userData.customerId,
                        emailHash: userData.emailHash.toLowerCase()
                    });
                } else {
                    window.ssp('set', 'user', {});
                }

                window.ssp('set', 'product', prefs.fpContext.product);
                window.ssp('start', 'fitpredictor');

                // Listen for size region, product, and cart events
                this.eventBus().on('size.tab.clicked', 'sizeRegionSelected');
                this.eventBus().on('product.updated', 'productUpdated');
                this.eventBus().on('product.added.to.cart', 'productAddedToCart');

                this.setupTrackingListeners();
            }
        }

        /**
         * @description Handles size region selection event.
         * @param activeTab - The activated tab identifier (e.g., 'size-EU')
         */
        sizeRegionSelected(activeTab): void {
            this.activeRegion = activeTab.split('-')[1];

            const productContext = JSON.parse(this.productContextString);

            productContext.sizeSystem = this.activeRegion;
            productContext.sizeConversion = productContext.gender + '-' + this.activeRegion;

            if (productContext && window.ssp) {
                window.ssp('set', 'product', productContext);
                window.ssp('start', 'fitpredictor');
            }
        }

        /**
         * @description Handles product update events and updates Fit Predictor context.
         * @param response - The event response containing fit predictor product context
         */
        productUpdated(response): void {
            let productContext = response.fitPredictor?.product;

            if (productContext && window.ssp) {
                this.productId = productContext.productId;

                if (this.activeRegion) {
                    productContext = {
                        ...productContext,
                        sizeSystem: this.activeRegion,
                        sizeConversion: productContext.gender + '-' + this.activeRegion
                    };
                }

                window.ssp('set', 'product', productContext);

                if (!productContext.size || this.sizeSelected === productContext.size) {
                    window.ssp('start', 'fitpredictor');
                }

                this.sizeSelected = productContext.size || '';
                this.productContextString = JSON.stringify(productContext);
            }
        }

        /**
         * @description Handles product added to cart event and tracks the action with Fit Predictor.
         * @param cart - The cart object containing the added product
         */
        productAddedToCart(cart): void {
            if (cart?.addedProduct && window.ssp) {
                window.ssp('track', 'addVariantToCart', {
                    productId: this.productId,
                    quantity: 1,
                    variantId: cart.addedProduct.id
                });
            }
        }

        /**
         * @description Assigns listeners to tracking events for the Fitpredictor widget on PDP.
         */
        // eslint-disable-next-line sonarjs/cognitive-complexity
        setupTrackingListeners(): void {
            if (window.ssp && window.dataLayer) {
                this.assignClickEvent();
                this.assignDoneEvent();
                this.assignLoadEvent();
                this.assignRecommendationEvents();
            }
        }

        /**
         * Assigns the click event subscription for FitPredictor tracking.
         */
        private assignClickEvent(): void {
            if (!window.ssp) {
                return;
            }

            window.ssp(
                'subscribe',
                'click',
                debounce(data => {
                    if (data.service !== 'fitpredictor') {
                        return;
                    }

                    if (data.state === 'cta') {
                        this.pushToDataLayer('click_calculate_size');
                    }

                    this.pushToDataLayer('display_popup');
                }, this.DEBOUNCE_DELAY)
            );
        }

        /**
         * Assigns the done event subscription for FitPredictor tracking.
         */
        private assignDoneEvent(): void {
            if (!window.ssp) {
                return;
            }

            window.ssp(
                'subscribe',
                'done',
                debounce(data => {
                    if (data.service !== 'fitpredictor') {
                        return;
                    }

                    if (data.state === 'size_prediction') {
                        this.pushToDataLayer('display_calculated_size', {
                            attributeName: 'value_dim',
                            attributeValue: this.getFormattedSize()
                        });
                    }
                }, this.DEBOUNCE_DELAY)
            );
        }

        /**
         * Assigns the load event subscription for FitPredictor tracking.
         */
        private assignLoadEvent(): void {
            if (!window.ssp) {
                return;
            }

            window.ssp(
                'subscribe',
                'load',
                debounce(data => {
                    if (data.service !== 'fitpredictor') {
                        return;
                    }

                    if (data.state === 'size_prediction') {
                        this.pushToDataLayer('display_calculated_size', {
                            attributeName: 'value_dim',
                            attributeValue: this.getFormattedSize()
                        });
                    }
                }, this.DEBOUNCE_DELAY)
            );
        }

        /**
         * Assigns recommendation event subscriptions for FitPredictor tracking.
         */
        private assignRecommendationEvents(): void {
            if (!window.ssp) {
                return;
            }

            window.ssp('subscribe', 'load', this.handleRecommendationEvent());
            window.ssp('subscribe', 'click', this.handleRecommendationEvent());
        }

        /**
         * @description retrieves and returns data that is common to all FP custom dataLayer events\
         * @returns an object containing the data
         */
        getEventCommonData(): TFitPredictorCommonEventData {
            const currentVariantId = document.querySelector('[data-current-product-id]')?.getAttribute('data-current-product-id');
            const baseProductId = this.productId;

            return {
                feature: 'fit_predictor',
                item_id: baseProductId || '',
                item_variant: currentVariantId || ''
            };
        }

        /**
         * @description Pushes properly formatted events to dataLayer
         * @param eventName the event name
         * @param details the details specific to this event
         */
        pushToDataLayer(eventName, details?): void {
            const eventCommonData = this.getEventCommonData();
            const data = {
                ...eventCommonData
            };

            if (details) {
                data[details.attributeName] = details.attributeValue;
            }

            window.dataLayer?.push({
                event: 'ga4_event',
                event_name: eventName,
                [eventName]: data
            });
        }

        /**
         * @description Retrieves and formats the suggested size by FitPredictor.
         * It translates to formats like "6-6.5_regular_women" for 1 gender and "12-12.5:10-10.5_regular_women:men" for 2 genders.
         * @returns the formatted value dim.
         */
        getFormattedSize(): string {
            let formattedSize = '';

            try {
                let sizeText = document.querySelector('.fp-size')?.textContent?.trim();
                const hasMultipleGenders = sizeText && sizeText.indexOf('/') !== -1;

                if (!sizeText) {
                    return formattedSize;
                }

                if (hasMultipleGenders) {
                    sizeText = sizeText?.replace('/[()]/g', '');
                    const [women, men] = (sizeText && sizeText.split('/')) || [];
                    const [womenSize] = women.split(' ');
                    const [menSize] = men.split(' ');
                    const width = sizeText?.toLowerCase().indexOf('narrow') !== -1 ? 'narrow' : 'regular';

                    formattedSize = `${womenSize}:${menSize}_${width}_women:men`;
                } else {
                    const predictionChunks = sizeText.split(' ');

                    // checking whether we have an exact size suggestion with no genders
                    if (predictionChunks.length <= 2) {
                        const [size, width] = predictionChunks;

                        formattedSize = size + ((width && '_' + width) || '');
                    } else {
                        const [size, gender, width] = predictionChunks;

                        formattedSize = `${size}_${width || ''}_${gender?.toLowerCase()?.replace(/[()]/g, '')}`;
                    }
                }
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (error) {
                return formattedSize;
            }

            return formattedSize;
        }

        /**
         * @description Retrieves and returns the formatted recommendation from FitPredictor.
         * @returns the formatted value dim.
         */
        getFormattedRecommendation() {
            let formattedRecommendation = '';

            try {
                const recommendationText = document.querySelector('.fp-recommendation')?.textContent?.trim();

                if (recommendationText) {
                    formattedRecommendation = recommendationText.toLowerCase().replace(' ', '_').trim();
                }
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (error) {
                return formattedRecommendation;
            }

            return formattedRecommendation;
        }

        /**
         * @description Handles the display recommendation event.
         * @returns a debounced callback
         */
        handleRecommendationEvent() {
            return debounce(data => {
                if (data.state === 'sud_prediction') {
                    this.pushToDataLayer('display_recommendation', {
                        attributeName: 'value_dim',
                        attributeValue: this.getFormattedRecommendation()
                    });
                }
            }, this.DEBOUNCE_DELAY);
        }
    }

    return FitPredictor;
}

export type TFitPredictorMixin = ReturnType<typeof FitPredictorClassCreator>;

export type TFitPredictorMixinInstance = InstanceType<TFitPredictorMixin>;

export default FitPredictorClassCreator;
